// /threads — the chat page shell (route target).
//
// It owns STATE and the stream; ThreadChatView owns the render. The split is
// the graph lane's (a fat shell + a stateless view): the page renders its
// view declaratively in the centre column, and the view pushes citations
// into the app right sidebar (SIDEBAR_LENS_REWORK — the sidebar starts below
// the page's top bar and shares space, so the transcript narrows but
// survives every citation open).
//
// Copy strategy per NEW_CHAT_PAGE_PLAN.md: fresh files, copy-adapted from the
// graph chat shell/panel, ZERO imports from them and zero edits to them. The
// mechanisms taken verbatim in behaviour are the 80ms token batching (per-token
// markdown reparse is O(n²)) and the transcript entry shape
// `{q, a, citations[], status: streaming|done|error|stopped}`.
//
// PERSISTENCE IS THE SERVER'S. The client sends no history — the ask route
// loads the thread's completed turns itself — so nothing here filters errored
// or stopped turns out of anything; they stay on screen as what happened.
import { useCallback, useEffect, useRef, useState } from "react";

import {
  askThreadStream,
  createThread,
  getThreadMessages,
} from "../../../api/threadsChat";
import { useDark } from "../../Universal/useNavTheme";
import { takePendingThreadSelect } from "./pendingSelect";

import ThreadChatView from "./ThreadChatView";

// The sidebar portal is the selector; the page is the surface. One event each
// way, both on `window` (the portal is mounted app-wide through a React portal,
// so there is no shared parent to hang context on — this is the house idiom,
// cf. "sidebar:report-selected").
const SELECT_EVENT = "sidebar:threads-thread-selected"; // detail {threadId|null}
const CHANGED_EVENT = "threads:changed"; // after create / rename / server title

// Citations arrive camelCase on the wire and may come back from Mongo as the
// stored document; normalise the one field every consumer indexes on.
const normalizeCitations = (rows) =>
  (Array.isArray(rows) ? rows : []).map((c) => ({
    ...c,
    documentId: c?.documentId || c?.document_id,
  }));

// A resumed turn is never "streaming" for US — that stream belonged to another
// page load. Keep what actually happened (error/stopped) and treat everything
// else as a finished turn.
const hydratedStatus = (status) =>
  status === "error" || status === "stopped" ? status : "done";

const MainThreads = () => {
  const dark = useDark();

  const [threadId, setThreadId] = useState(null); // null = draft (no thread yet)
  const [thread, setThread] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [scope, setScope] = useState("global");
  const [scopeMissing, setScopeMissing] = useState(false);
  // The composer's web toggle (plan Part 6/7 fallback arc, 2026-08-14). Lives
  // here like `scope` because it is a property of the next ask, and it is
  // STICKY within the open conversation (flagship behavior): once on, every
  // ask sends `web: true` — the server runs the search itself, guaranteed —
  // until the user turns it off or moves to another thread/draft
  // (`selectThread` resets it; it is never persisted or hydrated).
  // iso-fork: web is ON by default — every ask carries `web: true` for the
  // broader corpus+web answer unless the user switches it off.
  const [webSearch, setWebSearch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const transcriptRef = useRef([]);
  const threadIdRef = useRef(null);
  const scopeRef = useRef("global");
  const webSearchRef = useRef(true);
  const abortRef = useRef(null);
  const tokenBufRef = useRef("");
  const flushTimerRef = useRef(null);
  // Every async continuation is stamped with the selection it started under:
  // switching threads (or starting a draft) mid-flight must not let a late
  // response write into the conversation now on screen.
  const sessionRef = useRef(0);

  scopeRef.current = scope;
  webSearchRef.current = webSearch;

  // ── transcript mutation (single mutator: ref first, then state) ────────────
  const updateTranscript = useCallback((updater) => {
    transcriptRef.current = updater(transcriptRef.current);
    setTranscript(transcriptRef.current);
  }, []);

  // Token batching: tokens buffer here and flush at most every 80ms; non-token
  // events and stop force a flush first, so an entry is never patched with a
  // stale answer behind it.
  const flushTokens = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    const buf = tokenBufRef.current;
    if (!buf) return;
    tokenBufRef.current = "";
    updateTranscript((prev) => {
      if (!prev.length) return prev;
      const next = prev.slice();
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, a: last.a + buf };
      return next;
    });
  }, [updateTranscript]);

  const patchLast = useCallback(
    (patch) =>
      updateTranscript((prev) => {
        if (!prev.length) return prev;
        const next = prev.slice();
        next[next.length - 1] = { ...next[next.length - 1], ...patch };
        return next;
      }),
    [updateTranscript],
  );

  const cancelStream = useCallback(() => {
    flushTokens();
    tokenBufRef.current = "";
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    if (abortRef.current) abortRef.current();
    abortRef.current = null;
  }, [flushTokens]);

  // Nothing may outlive the page: a stream still writing into an unmounted
  // component is a memory leak with a React warning attached.
  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current();
      abortRef.current = null;
      clearTimeout(flushTimerRef.current);
    },
    [],
  );

  // ── thread selection / hydration ──────────────────────────────────────────
  const selectThread = useCallback(
    (id) => {
      // Re-selecting the thread already open is a no-op, never a reload: the
      // portal mirrors its active row off this same event, and the page emits
      // it after a lazy create — re-hydrating there would wipe a live stream
      // and refetch the turn currently being written.
      if (id && id === threadIdRef.current) return;
      sessionRef.current += 1;
      const seq = sessionRef.current;
      cancelStream();
      updateTranscript(() => []);
      setLoadError("");
      setScopeMissing(false);
      setScope("global");
      // The web toggle is per-conversation, never per-user: a new selection
      // (or a fresh draft) starts with it ON (the iso-fork default), whatever
      // the last thread did.
      setWebSearch(true);
      threadIdRef.current = id || null;
      setThreadId(id || null);
      setThread(null);

      if (!id) {
        // Draft: an empty conversation with no thread behind it. The thread is
        // created on the first send, so a draft that is never sent costs the
        // server nothing.
        setLoading(false);
        return;
      }

      setLoading(true);
      getThreadMessages(id)
        .then((payload) => {
          if (seq !== sessionRef.current) return;
          setThread(payload.thread || { thread_id: id });
          updateTranscript(() =>
            (payload.messages || []).map((m) => ({
              q: m.q || "",
              a: m.a || "",
              citations: normalizeCitations(m.citations),
              status: hydratedStatus(m.status),
              scope: m.scope,
              // Follow-ups are stored with the turn that produced them, so a
              // resumed conversation offers the same next questions it did
              // live. A server too old to store them sends nothing and the
              // row is simply absent.
              suggestions: Array.isArray(m.suggestions) ? m.suggestions : [],
              // Tool use (plan Part 6/7): `[{tool, query, results, elapsed_ms}]`
              // for turns that called a tool, MIXED — one turn's rows can name
              // any of search_web, read_url, search_corpus, read_citation,
              // entity_lookup, and `query` is the display string for that tool
              // (a query, a URL, "[2] Title", an entity name). Passed through
              // whole and grouped by `tool` in the view; nothing here switches
              // on the name, so a sixth tool arrives without a change. Same
              // defensive shape check as suggestions — absent on every turn that
              // used no tool, and on every turn at all against a server too old
              // to store it.
              toolUse: Array.isArray(m.tool_use) ? m.tool_use : [],
            })),
          );
          // The picker resumes where the thread left off. A dangling lens ref
          // (renamed or deleted preset) is Global AND SAYS SO — the server has
          // already fallen back, and a picker still showing the lens name
          // would be claiming a scope no answer was under.
          const last = payload.thread?.last_scope;
          if (payload.scope_missing || !last || last === "global" || !last.id) {
            setScope("global");
            setScopeMissing(!!payload.scope_missing);
          } else {
            setScope(last);
            setScopeMissing(false);
          }
        })
        .catch(() => {
          if (seq !== sessionRef.current) return;
          setLoadError("Couldn't load this conversation.");
        })
        .finally(() => {
          if (seq !== sessionRef.current) return;
          setLoading(false);
        });
    },
    [cancelStream, updateTranscript],
  );

  useEffect(() => {
    const handler = (e) => selectThread(e.detail?.threadId ?? null);
    window.addEventListener(SELECT_EVENT, handler);
    return () => window.removeEventListener(SELECT_EVENT, handler);
  }, [selectThread]);

  // A selection made from another route waits in pendingSelect.js until this
  // page exists (AnimatePresence delays the mount past the old route's exit
  // animation — no event dispatched before then has a listener). Consume it
  // here, AFTER the SELECT_EVENT subscription above, and replay it through
  // the same event so the portal's active row and the other sections'
  // active-section reset see exactly what a same-page click dispatches.
  useEffect(() => {
    const pending = takePendingThreadSelect();
    if (pending === undefined) return;
    window.dispatchEvent(
      new CustomEvent(SELECT_EVENT, { detail: { threadId: pending } }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "threads" },
      }),
    );
  }, []);

  // The sidebar renames threads too, and that title is the one this header
  // shows — so the header follows the same event the sidebar broadcasts.
  // A BARE event is the sidebar's; this page's own dispatches all carry a
  // detail, and refetching what we just wrote would be work for nothing.
  // Only the thread META is adopted: the transcript on screen is ours, and a
  // rename must never disturb an answer still streaming into it.
  useEffect(() => {
    const handler = (e) => {
      const id = threadIdRef.current;
      if (!id || e.detail) return;
      const seq = sessionRef.current;
      getThreadMessages(id)
        .then((payload) => {
          if (seq !== sessionRef.current || !payload.thread) return;
          setThread(payload.thread);
        })
        .catch(() => {
          /* cosmetic refresh — the conversation is unaffected */
        });
    };
    window.addEventListener(CHANGED_EVENT, handler);
    return () => window.removeEventListener(CHANGED_EVENT, handler);
  }, []);

  // ── ask ───────────────────────────────────────────────────────────────────
  const ask = useCallback(
    async (question) => {
      const q = String(question || "").trim();
      if (!q) return;
      const last = transcriptRef.current[transcriptRef.current.length - 1];
      if (last?.status === "streaming") return; // one stream at a time
      const seq = sessionRef.current;

      cancelStream();
      updateTranscript((prev) => [
        ...prev,
        { q, a: "", citations: [], status: "streaming" },
      ]);

      let id = threadIdRef.current;
      // Draft mode: the thread is born with the first question.
      if (!id) {
        try {
          const { thread: created } = await createThread();
          id = created?.thread_id;
          if (!id) throw new Error("no thread id");
          if (seq !== sessionRef.current) return; // user moved on mid-create
          threadIdRef.current = id;
          setThreadId(id);
          setThread(created);
          // The sidebar shows the thread the moment it exists, not only once
          // it has an answer...
          window.dispatchEvent(
            new CustomEvent(CHANGED_EVENT, { detail: { threadId: id } }),
          );
          // ...and its active row follows the SELECTION event, which the portal
          // mirrors rather than owns. Selecting the thread we just created is
          // what keeps "new conversation → first send" from leaving the list
          // with nothing highlighted. `selectThread` ignores it (same id).
          window.dispatchEvent(
            new CustomEvent(SELECT_EVENT, { detail: { threadId: id } }),
          );
        } catch {
          if (seq !== sessionRef.current) return;
          patchLast({
            status: "error",
            error: "Couldn't start a new conversation.",
          });
          return;
        }
      }

      // Creating the thread is a round trip, and Stop is live for the whole of
      // it (the turn is already on screen as streaming). If the user took it,
      // the turn is `stopped` — starting the stream now would answer a question
      // they cancelled. The thread stays: it exists, and the sidebar has it.
      if (transcriptRef.current[transcriptRef.current.length - 1]?.status
        !== "streaming") {
        return;
      }

      // The gate every callback below runs through. TWO conditions, because a
      // stream can stop being ours in two ways: the user selected another
      // thread (the sequence moved), or they pressed Stop (the turn is no
      // longer streaming). An abort is detected on the next read, not
      // instantly, so an event parsed in the chunk already in hand can still
      // arrive after either — and a late `done` must not un-stop a stopped
      // turn, nor late tokens append to it.
      const live = () =>
        seq === sessionRef.current &&
        transcriptRef.current[transcriptRef.current.length - 1]?.status ===
          "streaming";

      abortRef.current = askThreadStream(
        // `web` rides per-ask off the sticky toggle. When the server can't
        // honor it (no ZYTE_KEY) the 400's message arrives through onError
        // below and the turn shows the inline error — accepted v1 (the UI
        // can't know the server's config, and silence would be worse).
        {
          threadId: id,
          question: q,
          scope: scopeRef.current,
          web: webSearchRef.current,
        },
        {
          // A FACT about the phase the server is in: it labels the spinner and
          // nothing waits on it. A server too old to send one leaves the bare
          // spinner. Handed down whole — the view is tolerant of its shape.
          //
          // TOOL USE RIDES THIS EVENT AND NO OTHER (plan Part 6/7). Every
          // tool's stage arrives as an ordinary status event — searching /
          // reading / the corpus, citation and entity stages the 5-tool toolbox
          // added — each carrying its human-readable text in `message`, so the
          // live turn narrates whatever it is doing through the spinner label
          // already, with no handler here and no list of stage names anywhere
          // in this file.
          // There is deliberately NO `tool_use` event on the wire: the tool
          // record is PERSISTED with the message, which splits the feature in
          // two by design —
          //   · live turn      → the status line, while it is happening;
          //   · resumed turn   → the per-tool "Searched / Read / Looked up …"
          //                      annotation lines, hydrated above.
          // Reconstructing the annotation live by scraping a query out of the
          // status prose would be guessing at a string the server formats for
          // humans; the transcript reload is the one source of that fact.
          //
          // `tool_rounds` on the `done` event is the ACTUAL number of
          // executed tool rounds this turn, 0..3 (the 2026-08-14 meaning
          // change; it was a 0|1 "used any tool" flag when search_web was
          // the only tool — see routes/v2/chat/threads.py's event doc).
          // Nothing on this page reads it beyond truthiness — the
          // annotation comes from the persisted rows, not the count — which
          // is why the meaning could change without a code change here.
          onStatus: (e) => {
            if (!live()) return;
            patchLast({ statusLine: e });
          },
          onCitations: (e) => {
            if (!live()) return;
            flushTokens();
            patchLast({
              citations: normalizeCitations(e?.docs),
              retrieval: e?.retrieval,
            });
          },
          // The client resolves a token to text either way: the stream module
          // hands the string, and an event object would be the obvious thing
          // for it to hand instead one day.
          onToken: (e) => {
            if (!live()) return;
            tokenBufRef.current += typeof e === "string" ? e : String(e?.text || "");
            if (!flushTimerRef.current) {
              flushTimerRef.current = setTimeout(flushTokens, 80);
            }
          },
          // THE TITLE IS THE SERVER'S. It arrives twice on a thread's first
          // ask — provisional within the first second, final after the model
          // has titled it — so "New thread" is never what a real conversation
          // is called for longer than that, here or in the sidebar list.
          //
          // Gated on the SESSION ONLY, not on `live()`: the final title rides
          // the stream tail and the turn it belongs to may already be done (or
          // stopped) by then. It is thread meta, not turn content — a stopped
          // turn's thread still earns its name.
          onTitle: (e) => {
            if (seq !== sessionRef.current) return;
            const title = e?.title;
            if (!title) return;
            setThread((prev) =>
              prev ? { ...prev, title } : { thread_id: id, title },
            );
            // WITH A DETAIL, always: the sidebar refetches on any
            // `threads:changed`, while this page's own meta-refresh listens
            // for the BARE event only. A bare dispatch here would have us
            // refetch the title we were just handed.
            window.dispatchEvent(
              new CustomEvent(CHANGED_EVENT, { detail: { threadId: id } }),
            );
          },
          // Follow-ups belong to the turn that produced them, not to the page:
          // stored on the streaming entry, they survive the next question and
          // come back with the transcript on resume.
          onSuggestions: (questions) => {
            if (!live()) return;
            patchLast({ suggestions: questions });
          },
          onDone: (e) => {
            if (!live()) return;
            flushTokens();
            abortRef.current = null;
            patchLast({ status: "done", truncated: e?.truncated });
          },
          onError: (e) => {
            if (!live()) return;
            flushTokens();
            abortRef.current = null;
            patchLast({
              status: "error",
              error:
                (typeof e === "string" ? e : e?.message) || "Something went wrong.",
            });
          },
        },
      );
    },
    [cancelStream, flushTokens, patchLast, updateTranscript],
  );

  // Stop = flush what arrived, abort, and keep the partial answer. The turn is
  // marked `stopped` rather than deleted: it is what the user saw.
  const stop = useCallback(() => {
    cancelStream();
    updateTranscript((prev) => {
      if (!prev.length || prev[prev.length - 1].status !== "streaming") {
        return prev;
      }
      const next = prev.slice();
      next[next.length - 1] = { ...next[next.length - 1], status: "stopped" };
      return next;
    });
  }, [cancelStream, updateTranscript]);

  // Regenerate APPENDS — the first answer stays readable beside the new one,
  // and the server records it as another turn, which is the truth of it.
  const regenerate = useCallback(() => {
    const last = transcriptRef.current[transcriptRef.current.length - 1];
    if (!last?.q || last.status === "streaming") return;
    ask(last.q);
  }, [ask]);

  // Rename lives in the sidebar list now (the page has no title bar); its
  // bare `threads:changed` dispatch is how a new title reaches this page.

  const busy = transcript[transcript.length - 1]?.status === "streaming";

  return (
    <ThreadChatView
      thread={thread}
      isDraft={!threadId}
      loading={loading}
      loadError={loadError}
      transcript={transcript}
      busy={busy}
      scope={scope}
      scopeMissing={scopeMissing}
      onScopeChange={(next) => {
        setScope(next);
        // Picking a lens answers the "(lens deleted)" notice — the thread is
        // no longer resuming a scope that vanished.
        setScopeMissing(false);
      }}
      webSearch={webSearch}
      onWebSearchChange={setWebSearch}
      onAsk={ask}
      onStop={stop}
      onRegenerate={regenerate}
      dark={dark}
    />
  );
};

export default MainThreads;
